import { createApi } from '@reduxjs/toolkit/query/react';
import config from 'config/index';
import axiosBaseQuery from 'rtk/query/axiosBaseQuery';
import { EUserType } from 'types/accessControll.types';
// types
import type { TRichTextContentValue } from 'types/general.types';
import type { TemplateComment } from 'types/template/templatesComments.types';

const reducerPath = 'template-comments' as const;
const path = 'v1/template-comments' as const;

export const templateCommentsApi = createApi({
  reducerPath: reducerPath,
  baseQuery: axiosBaseQuery({
    baseUrl: `${config.VITE_BACKEND_URL}/${path}`,
  }),
  endpoints: (builder) => ({
    getTemplateCommentByIds: builder.query<
      { templateComment: TemplateComment },
      {
        clientId: string;
        templateReferenceId: string;
        nodeId: string;
        questionPath?: string;
      }
    >({
      query: (x) => ({
        url: `/comment-by-ids`,
        method: 'POST',
        data: x,
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
    }),

    postComment: builder.mutation<
      { comments: any[] },
      {
        clientId: string;
        templateReferenceId: string;
        nodeId: string;
        questionPath?: string;
        contentValue: TRichTextContentValue;
      }
    >({
      query: (x) => ({
        url: `/comment`,
        method: 'POST',
        data: x,
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
    }),

    markCommentAsRead: builder.mutation<{ success: true }, { comment: TemplateComment }>({
      query: (x) => ({
        url: `/mark-as-read`,
        method: 'POST',
        data: x,
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
    }),

    markAsResolved: builder.mutation<
      { success: true },
      {
        templateCommentId: string;
        isResolved: boolean;
      }
    >({
      query: (x) => ({
        url: `/mark-as-resolved`,
        method: 'POST',
        data: x,
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
    }),

    requestQuestionRemoval: builder.mutation<
      { success: true },
      {
        clientId: string;
        templateReferenceId: string;
        nodeId: string;
        questionPath?: string;
      }
    >({
      query: (x) => ({
        url: `/request-question-removal`,
        method: 'POST',
        data: x,
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
    }),

    deleteComment: builder.mutation<
      { success: true },
      {
        templateCommentId: string;
        commentIndex: number;
      }
    >({
      query: (x) => ({
        url: `/delete-comment`,
        method: 'POST',
        data: x,
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
    }),
  }),
});

export const {
  // Queries
  useGetTemplateCommentByIdsQuery,
  // Mutations
  usePostCommentMutation,

  useMarkAsResolvedMutation,
  useRequestQuestionRemovalMutation,
  useDeleteCommentMutation,
} = templateCommentsApi;
